import React, { useState, useEffect } from 'react';
import { getParts, buyPart, getAvailableDronePurchases, purchaseScoutDrone, purchaseAssaultDrone, purchaseNukeDrone, purchaseMaritimeScoutDrone, checkDronePurchaseLimit } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import './Parts.css';

// Mining rate constants - must match server values
// New calculation: 25 base H/s + (parts * 5 H/s), 75 H/s = 0.000400 coins/sec
const BASE_HASH_RATE = 25; // Base hash rate in H/s
const HASH_RATE_PER_PART = 5; // Each part adds 5 H/s
const COINS_PER_HASH_RATE = 0.00000533333; // Coins per second per H/s (0.000400 / 75)

const Parts = () => {
  const { user, updateUser, syncData } = useAuth();
  const { notifyPartPurchase } = useNotifications();
  const [miningParts, setMiningParts] = useState([]);
  const [bruteForceParts, setBruteForceParts] = useState([]);
  const [networkParts, setNetworkParts] = useState([]);
  const [securityParts, setSecurityParts] = useState([]);
  const [malwareParts, setMalwareParts] = useState([]);
  const [snifferParts, setSnifferParts] = useState([]);
  const [scannerParts, setScannerParts] = useState([]);
  const [defenseSystemParts, setDefenseSystemParts] = useState([]);
  const [offenseDroneParts, setOffenseDroneParts] = useState([]);
  const [availableDronePurchases, setAvailableDronePurchases] = useState([]);
  const [droneLoading, setDroneLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState({
    message: '',
    type: '', // 'success' or 'error'
    visible: false
  });
  const [activeTab, setActiveTab] = useState('mining');
  const [lastPurchaseTime, setLastPurchaseTime] = useState(0);
  const [purchasingPartId, setPurchasingPartId] = useState(null);

  // Calculate current mining rate based on owned parts
  const getCurrentMiningRate = () => {
    const ownedMiningParts = user?.miningRigInventory?.length || 0;
    const currentHashRate = BASE_HASH_RATE + (ownedMiningParts * HASH_RATE_PER_PART);
    return currentHashRate * COINS_PER_HASH_RATE;
  };

  // Calculate future mining rate if user buys a part
  const getFutureMiningRate = () => {
    const ownedMiningParts = user?.miningRigInventory?.length || 0;
    const futureHashRate = BASE_HASH_RATE + ((ownedMiningParts + 1) * HASH_RATE_PER_PART);
    return futureHashRate * COINS_PER_HASH_RATE;
  };

  // Format mining rate for display
  const formatMiningRate = (rate) => {
    return (rate * 3600).toFixed(6); // Convert to per hour for readability
  };

  useEffect(() => {
    fetchParts();
    fetchDronePurchases();
  }, [user]);

  const fetchDronePurchases = async () => {
    try {
      setDroneLoading(true);
      const droneData = await getAvailableDronePurchases();
      setAvailableDronePurchases(droneData.dronePurchases || []);
    } catch (err) {
      console.error('Error fetching drone purchases:', err);
    } finally {
      setDroneLoading(false);
    }
  };

  const fetchParts = async () => {
    try {
      setLoading(true);
      const partsData = await getParts();
      
      if (Array.isArray(partsData)) {
        // Separate parts by category
        const mining = partsData.filter(part => part.category === 'mining' || !part.category);
        const bruteforce = partsData.filter(part => part.category === 'bruteforce');
        const network = partsData.filter(part => part.category === 'network');
        const security = partsData.filter(part => part.category === 'security');
        const malware = partsData.filter(part => part.category === 'malware');
        const sniffer = partsData.filter(part => part.category === 'sniffer');
        const scanner = partsData.filter(part => part.category === 'scanner');
        const defenseSystems = partsData.filter(part => part.category === 'defense_systems');
        const offenseDrones = partsData.filter(part => part.category === 'offense_drones');
        
        // Process mining parts
        const processedMiningParts = mining.map(part => ({
          ...part,
          icon: getIconByType(part),
          owned: user?.miningRigInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));

        // Process brute force parts
        const processedBruteForceParts = bruteforce.map(part => ({
          ...part,
          icon: getBruteForceIcon(part),
          owned: user?.bruteForceInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));

        // Process network parts
        const processedNetworkParts = network.map(part => ({
          ...part,
          icon: getNetworkIcon(part),
          owned: user?.partsInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));

        // Process security parts
        const processedSecurityParts = security.map(part => ({
          ...part,
          icon: getSecurityIcon(part),
          owned: user?.partsInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));

        // Process malware parts
        const processedMalwareParts = malware.map(part => ({
          ...part,
          icon: getMalwareIcon(part),
          owned: user?.malwareInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));
        
        // Process sniffer parts
        const processedSnifferParts = sniffer.map(part => ({
          ...part,
          icon: getSnifferIcon(part),
          owned: user?.snifferInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));

        // Process scanner parts
        const processedScannerParts = scanner.map(part => ({
          ...part,
          icon: getScannerIcon(part),
          owned: user?.scannerInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));
        
        // Process ground warfare parts
        const processedDefenseSystemParts = defenseSystems.map(part => ({
          ...part,
          icon: getDefenseSystemIcon(part),
          owned: user?.partsInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));

        // Process offense drone parts
        const processedOffenseDroneParts = offenseDrones.map(part => ({
          ...part,
          icon: part.icon || getOffenseDroneIcon(part),
          owned: user?.droneInventory?.some(p => 
            p._id === part._id || 
            p.partId === part._id || 
            (p.partId && p.partId.toString() === part._id.toString()) ||
            p.name === part.name
          ) || false,
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        }));

        
        setMiningParts(processedMiningParts);
        setBruteForceParts(processedBruteForceParts);
        setNetworkParts(processedNetworkParts);
        setSecurityParts(processedSecurityParts);
        setMalwareParts(processedMalwareParts);
        setSnifferParts(processedSnifferParts);
        setScannerParts(processedScannerParts);
        setDefenseSystemParts(processedDefenseSystemParts);
        setOffenseDroneParts(processedOffenseDroneParts);
      }
    } catch (err) {
      console.error('Error fetching parts:', err);
      setError('Failed to load parts');
    } finally {
      setLoading(false);
    }
  };

  const getIconByType = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Mining Rig Parts - Specific icons for each part
    if (name.includes('basic cpu upgrade')) return '🖥️';
    if (name.includes('standard ram module')) return '💾';
    if (name.includes('gaming gpu')) return '🎮';
    if (name.includes('liquid cooling system')) return '❄️';
    if (name.includes('modular power supply')) return '⚡';
    if (name.includes('extended battery bank')) return '🔋';
    if (name.includes('turbine cooling array')) return '🌀';
    if (name.includes('premium ddr5 ram')) return '💿';
    if (name.includes('quantum processing unit')) return '🚀';
    if (name.includes('mining rig controller')) return '⚙️';
    
    // Fallback for mining parts by type
    if (part.category === 'mining') {
      const type = (part.type || '').toUpperCase();
      if (type === 'CPU') return '🖥️';
      if (type === 'RAM') return '💾';
      if (type === 'GPU') return '🎮';
      if (type === 'COOLING') return '❄️';
      if (type === 'POWER') return '⚡';
    }
    
    return '🔧'; // Default mining icon
  };

  const getBruteForceIcon = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Brute Force Parts - Specific icons for each part
    if (name.includes('quantum processor array')) return '⚛️';
    if (name.includes('neural network accelerator')) return '🧠';
    if (name.includes('cryptographic asic miner')) return '💎';
    if (name.includes('parallel computing cluster')) return '🌐';
    if (name.includes('rainbow table generator')) return '🌈';
    if (name.includes('supercomputer interface')) return '🚀';
    if (name.includes('molecular computer lab')) return '🔬';
    if (name.includes('laser attack module')) return '🎯';
    if (name.includes('time acceleration engine')) return '⚙️';
    if (name.includes('universal decryption tool')) return '🛠️';
    
    return '💻'; // Default brute force icon
  };

  const getNetworkIcon = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Network Injection Parts - Specific icons for each part
    if (name.includes('basic packet injector')) return '💉';
    if (name.includes('wifi signal amplifier')) return '📡';
    if (name.includes('protocol flooding engine')) return '🌊';
    if (name.includes('man-in-the-middle router')) return '📦';
    if (name.includes('botnet command module')) return '💀';
    if (name.includes('zero-day exploit kit')) return '⚔️';
    if (name.includes('ai network spider')) return '🕷️';
    if (name.includes('social engineering toolkit')) return '🎭';
    if (name.includes('quantum network tunneler')) return '🌀';
    if (name.includes('neural virus generator')) return '🔥';
    
    return '🌊'; // Default network injection icon
  };

  const getSecurityIcon = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Firewall Defense Parts - Specific icons for each part
    if (name.includes('basic firewall module')) return '🛡️';
    if (name.includes('enhanced packet filter')) return '🔥';
    if (name.includes('fortress wall system')) return '🚐';
    if (name.includes('diamond barrier protocol')) return '💎';
    if (name.includes('ai security sentinel')) return '🤖';
    if (name.includes('quantum encryption lock')) return '🔒';
    if (name.includes('lightning rod defense')) return '⚡';
    if (name.includes('stellar shield array')) return '🌟';
    if (name.includes('reality distortion field')) return '🔮';
    if (name.includes('eternal guardian matrix')) return '🗿';
    
    return '🛡️'; // Default firewall icon
  };

  const getMalwareIcon = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Malware Parts - Specific icons for each part
    if (name.includes('basic rootkit compiler')) return '🦠';
    if (name.includes('advanced payload encoder')) return '🔐';
    if (name.includes('polymorphic code generator')) return '🧬';
    if (name.includes('exploit development kit')) return '⚔️';
    if (name.includes('anti-forensics module')) return '🕵️';
    if (name.includes('neural network fuzzer')) return '🧠';
    if (name.includes('quantum encryption bypass')) return '⚛️';
    if (name.includes('memory injection framework')) return '💉';
    if (name.includes('zero-day exploitation suite')) return '💣';
    if (name.includes('steganographic delivery system')) return '🎭';
    
    return '🦠'; // Default malware icon
  };

  const getSnifferIcon = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Sniffer Tool Parts - Different icons for each part
    if (name.includes('signal amplifier')) return '📡';
    if (name.includes('quantum antenna')) return '🛰️';
    if (name.includes('neural network processor')) return '🧠';
    if (name.includes('electromagnetic field')) return '⚡';
    if (name.includes('satellite uplink')) return '🛰️';
    if (name.includes('cryogenic signal cooler')) return '❄️';
    if (name.includes('plasma resonance')) return '🔬';
    if (name.includes('gravitational wave')) return '🌌';
    if (name.includes('dark matter scanner')) return '🕳️';
    if (name.includes('dimensional phase shifter')) return '🌀';
    
    return '📡'; // Default sniffer icon
  };

  const getScannerIcon = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Scanner Tool Parts - Different icons for each part
    if (name.includes('quantum processing')) return '🧠';
    if (name.includes('neural network')) return '🤖';
    if (name.includes('multi-core')) return '🖥️';
    if (name.includes('memory buffer')) return '💾';
    if (name.includes('cryptographic')) return '🔐';
    if (name.includes('pattern matcher')) return '🎯';
    if (name.includes('signal processing')) return '📡';
    if (name.includes('exploit database')) return '📊';
    if (name.includes('stealth scan')) return '🕵️';
    if (name.includes('quantum entanglement')) return '✨';
    
    return '🔍'; // Default scanner icon
  };

  const getDefenseSystemIcon = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Defense System Parts - Specific icons for each part
    if (name.includes('mup5 fortress core')) return '🚐';
    if (name.includes('mup5 defense tower')) return '🗼';
    if (name.includes('mup5 radar station')) return '📡';
    if (name.includes('ocean surveillance satellite')) return '🛰️';
    if (name.includes('automated defense grid')) return '🤖';
    if (name.includes('signal jammer')) return '📶';
    if (name.includes('emp shield generator')) return '🛡️';
    if (name.includes('quantum encryption hub')) return '🔐';
    
    return '🛡️'; // Default defense system icon
  };

  const getOffenseDroneIcon = (part) => {
    const name = (part.name || '').toLowerCase();
    
    // Offense Drone Parts - Specific icons for each part
    if (name.includes('assault drone mk1')) return '🚁';
    if (name.includes('stealth drone mk2')) return '🛸';
    if (name.includes('heavy assault drone')) return '🚀';
    if (name.includes('swarm drone pack')) return '🎯';
    
    return '🚁'; // Default drone icon
  };



  const handleBuy = async (part) => {
    // Prevent multiple simultaneous purchases
    if (purchasingPartId) {
      // Silently prevent multiple simultaneous purchases
      return;
    }

    // Debounce purchases - prevent rapid clicking
    const now = Date.now();
    if (now - lastPurchaseTime < 2000) { // 2 second debounce
      // Silently prevent rapid purchases without console spam
      return;
    }
    setLastPurchaseTime(now);

    if (!part.affordable) {
      setPurchaseStatus({
        message: `Insufficient WR Coins. You need ${part.cost} coins but only have ${user?.wrCoins || 0}.`,
        type: 'error',
        visible: true
      });
      setTimeout(() => setPurchaseStatus(prev => ({ ...prev, visible: false })), 3000);
      return;
    }

    if (!part.unlocked) {
      setPurchaseStatus({
        message: `Insufficient XP. You need ${part.xpRequired} XP but only have ${user?.xp || 0}.`,
        type: 'error',
        visible: true
      });
      setTimeout(() => setPurchaseStatus(prev => ({ ...prev, visible: false })), 3000);
      return;
    }

    // Set purchasing state
    setPurchasingPartId(part._id);
    setPurchaseStatus({
      message: `Purchasing ${part.name}...`,
      type: 'info',
      visible: true
    });

    try {
      // Only log purchases in development mode to reduce console spam
      if (process.env.NODE_ENV === 'development') {
        console.log('Purchasing part:', part.name);
        console.log('User stats:', { 
          wrCoins: user?.wrCoins || 0, 
          xp: user?.xp || 0 
        });
        console.log('Part requirements:', { 
          cost: part.cost, 
          xpRequired: part.xpRequired || 0 
        });
        console.log('Affordability check:', {
          affordable: (user?.wrCoins || 0) >= part.cost,
          unlocked: (user?.xp || 0) >= (part.xpRequired || 0)
        });
      }
      const result = await buyPart(part._id);
      
      if (result.success) {
        // Update user coins and network/security stats
        const newCoins = (user?.wrCoins || 0) - part.cost;
        const updatedUserData = { 
          wrCoins: newCoins,
          networkInjectorTools: result.networkInjectorTools || user?.networkInjectorTools || 0,
          firewallParts: result.firewallParts || user?.firewallParts || 0
        };
        
        // Update inventory counts if they were returned from the server
        if (result.bruteForceInventoryCount !== undefined) {
          // For brute force parts, we need to update the bruteForceInventory array
          updatedUserData.bruteForceInventory = user?.bruteForceInventory || [];
          // Add the new part to the local inventory
          updatedUserData.bruteForceInventory.push(result.part);
        }
        
        if (result.malwareInventoryCount !== undefined) {
          // For malware parts, we need to update the malwareInventory array
          updatedUserData.malwareInventory = user?.malwareInventory || [];
          // Add the new part to the local inventory
          updatedUserData.malwareInventory.push(result.part);
        }
        
        if (result.snifferInventoryCount !== undefined) {
          // For sniffer parts, we need to update the snifferInventory array
          updatedUserData.snifferInventory = user?.snifferInventory || [];
          // Add the new part to the local inventory
          updatedUserData.snifferInventory.push(result.part);
        }
        
        if (result.scannerInventoryCount !== undefined) {
          // For scanner parts, we need to update the scannerInventory array
          updatedUserData.scannerInventory = user?.scannerInventory || [];
          // Add the new part to the local inventory
          updatedUserData.scannerInventory.push(result.part);
        }
        
        // Ground warfare inventory updates
        if (result.defenseInventoryCount !== undefined) {
          updatedUserData.defenseInventory = user?.defenseInventory || [];
          updatedUserData.defenseInventory.push(result.part);
        }
        
        if (result.droneInventoryCount !== undefined) {
          updatedUserData.droneInventory = user?.droneInventory || [];
          updatedUserData.droneInventory.push(result.part);
        }
        
        const excludedCategories = ['bruteforce', 'malware', 'sniffer', 'scanner', 'defense_systems', 'offense_drones'];
        if (result.partsInventoryCount !== undefined && !excludedCategories.includes(part.category)) {
          // For other parts (mining, network, security), update the partsInventory array
          updatedUserData.partsInventory = user?.partsInventory || [];
          updatedUserData.partsInventory.push(result.part);
        }
        
        updateUser(updatedUserData);
        
        // Force component re-render by updating a timestamp state
        setLastPurchaseTime(Date.now());
        
        // Sync latest user data from server to ensure accuracy
        setTimeout(async () => {
          try {
            await syncData();
          } catch (err) {
            console.log('Post-purchase sync failed, but purchase was successful');
          }
        }, 500);
        
        // Add notification for successful purchase
        notifyPartPurchase(part.name, part.cost, part.category);
        
        // Update part ownership and refresh parts
        await fetchParts();
        
        setPurchaseStatus({
          message: `Successfully purchased ${part.name}!`,
          type: 'success',
          visible: true
        });
      } else {
        setPurchaseStatus({
          message: result.message || 'Purchase failed',
          type: 'error',
          visible: true
        });
      }
    } catch (err) {
      console.error('Purchase error:', err);
      console.error('Error response:', err.response?.data);
      // Extract the error message from the server response
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Purchase failed. Please try again.';
      
      // Show an alert for immediate user feedback
      alert(`Purchase Failed: ${errorMessage}`);
      
      setPurchaseStatus({
        message: errorMessage,
        type: 'error',
        visible: true
      });
    } finally {
      // Always clear purchasing state
      setPurchasingPartId(null);
    }
    
    setTimeout(() => setPurchaseStatus(prev => ({ ...prev, visible: false })), 3000);
  };

  const handleDronePurchase = async (droneType) => {
    try {
      setDroneLoading(true);
      let result;
      
      switch (droneType) {
        case 'scout_drone':
          result = await purchaseScoutDrone();
          break;
        case 'assault_drone':
          result = await purchaseAssaultDrone();
          break;
        case 'nuke_drone':
          result = await purchaseNukeDrone();
          break;
        case 'maritime_scout_drone':
          result = await purchaseMaritimeScoutDrone();
          break;
        default:
          throw new Error('Unknown drone type');
      }

      if (result.message) {
        // Update user data
        const updatedUserData = {
          xp: result.remainingResources.xp,
          wrCoins: result.remainingResources.wrCoins,
          droneInventory: user?.droneInventory || []
        };
        updatedUserData.droneInventory.push(result.drone);
        
        updateUser(updatedUserData);
        
        // Force component re-render by updating a timestamp state
        setLastPurchaseTime(Date.now());
        
        // Sync latest user data from server to ensure accuracy
        setTimeout(async () => {
          try {
            await syncData();
          } catch (err) {
            console.log('Post-purchase sync failed, but purchase was successful');
          }
        }, 500);
        
        // Refresh drone purchase data
        await fetchDronePurchases();
        
        setPurchaseStatus({
          message: result.message,
          type: 'success',
          visible: true
        });
      }
    } catch (err) {
      console.error('Drone purchase error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to purchase drone. Please try again.';
      setPurchaseStatus({
        message: errorMessage,
        type: 'error',
        visible: true
      });
    } finally {
      setDroneLoading(false);
      setTimeout(() => setPurchaseStatus(prev => ({ ...prev, visible: false })), 5000);
    }
  };

  const renderDroneCard = (drone) => (
    <div key={drone.id} className={`part-card ${!drone.available ? 'locked' : ''} ${!drone.canAfford ? 'expensive' : ''}`}>
      <div className="part-icon">
        {drone.id === 'scout_drone' ? '✈️' : 
         drone.id === 'assault_drone' ? '✈️' : 
         drone.id === 'maritime_scout_drone' ? '🛥️' : 
         drone.id === 'nuke_drone' ? '☢️' : '✈️'}
      </div>
      <h3 className="part-name">{drone.name}</h3>
      <p className="part-description">{drone.description}</p>
      
      <div className="part-stats">
        <div className="drone-specs">
          <span className="spec-item">🎯 {drone.specifications.attackPower}</span>
          <span className="spec-item">📏 {drone.specifications.range}</span>
          <span className="spec-item">⏱️ {drone.specifications.duration}</span>
          {drone.specifications.stealth && <span className="spec-item">👤 {drone.specifications.stealth}</span>}
        </div>
      </div>
      
      <div className="part-requirements">
        <div className={`cost ${drone.canAfford ? 'affordable' : 'expensive'}`}>
          💰 {drone.coinCost} WR Coins
        </div>
        <div className={`xp-req ${drone.canAfford ? 'unlocked' : 'locked'}`}>
          ⭐ {drone.xpCost} XP Required
        </div>
      </div>
      
      {drone.requirements && (
        <div className="requirements-warning">
          ⚠️ {drone.requirements}
        </div>
      )}
      
      {!drone.available ? (
        <button className="part-button locked" disabled>
          🔒 {drone.requirements || 'Unavailable'}
        </button>
      ) : !drone.canAfford ? (
        <button className="part-button expensive" disabled>
          💸 Cannot Afford
        </button>
      ) : (
        <button 
          className="part-button available" 
          onClick={() => handleDronePurchase(drone.id)}
          disabled={droneLoading}
        >
          {droneLoading ? '⏳ Purchasing...' : '🛒 Purchase Drone'}
        </button>
      )}
    </div>
  );

  const renderPartCard = (part) => (
    <div key={part._id} className={`part-card ${part.owned ? 'owned' : ''} ${!part.unlocked ? 'locked' : ''}`}>
      <div className="part-icon">{part.icon}</div>
      <h3 className="part-name">{part.name}</h3>
      <p className="part-description">{part.description}</p>
      
      {part.category === 'mining' ? (
        <div className="part-stats">
          <span className="efficiency-boost">+{formatMiningRate(HASH_RATE_PER_PART * COINS_PER_HASH_RATE)} WR/hr</span>
        </div>
      ) : part.category === 'bruteforce' ? (
        <div className="part-stats">
          <span className="brute-force-reduction">-{part.bruteForceReduction || 1} Days Brute Force</span>
        </div>
      ) : part.category === 'network' ? (
        <div className="part-stats">
          <span className="network-reduction">-{part.networkReduction || 1} Hour Attack Time</span>
        </div>
      ) : part.category === 'security' ? (
        <div className="part-stats">
          <span className="firewall-protection">-{part.firewallProtection || 1}% Stolen Coins</span>
        </div>
      ) : part.category === 'malware' ? (
        <div className="part-stats">
          <span className="malware-reduction">-{Math.round((part.malwareReduction || 0.5) * 60)} Minutes Attack Time</span>
        </div>
      ) : part.category === 'sniffer' ? (
        <div className="part-stats">
          <span className="sniffer-boost">+{part.snifferRangeBoost || 600}m Sniffer Range</span>
        </div>
      ) : part.category === 'scanner' ? (
        <div className="part-stats">
          <span className="scanner-reduction">-{part.scannerReduction || 15} Minutes Scan Time</span>
        </div>
      ) : part.category === 'defense_systems' ? (
        <div className="part-stats">
          <span className="defense-system">Defense System Equipment</span>
        </div>
      ) : part.category === 'electronic_warfare' ? (
        <div className="part-stats">
          <span className="electronic-warfare">Electronic Warfare</span>
        </div>
      ) : part.category === 'upgrades' ? (
        <div className="part-stats">
          <span className="upgrade-package">Upgrade Package</span>
        </div>
      ) : (
        <div className="part-stats">
          <span className="generic-boost">Special Equipment</span>
        </div>
      )}
      
      <div className="part-requirements">
        <div className={`cost ${part.affordable ? 'affordable' : 'expensive'}`}>
          💰 {part.cost} WR Coins
        </div>
        {part.xpRequired > 0 && (
          <div className={`xp-req ${part.unlocked ? 'unlocked' : 'locked'}`}>
            ⭐ {part.xpRequired} XP Required
          </div>
        )}
      </div>
      
      {part.owned ? (
        <button className="part-button owned" disabled>
          ✅ Owned
        </button>
      ) : !part.unlocked ? (
        <button className="part-button locked" disabled>
          🔒 Locked
        </button>
      ) : !part.affordable ? (
        <button className="part-button expensive" disabled>
          💸 Too Expensive
        </button>
      ) : (
        <button 
          className={`part-button ${purchasingPartId === part._id ? 'purchasing' : 'available'}`}
          onClick={() => handleBuy(part)}
          disabled={purchasingPartId === part._id}
        >
          {purchasingPartId === part._id ? '⏳ Purchasing...' : '🛒 Buy Now'}
        </button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="parts-container">
        <div className="loading">Loading computer parts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="parts-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="parts-container">
      <div className="content-section">
        <div className="content-section-header">
          <h2>🛠️ Cyber Warfare Market</h2>
          <p>Upgrade your mining rig and hacking tools with specialized computer parts.</p>
        </div>
      
      {/* Part Category Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'mining' ? 'active' : ''}`}
          onClick={() => setActiveTab('mining')}
        >
          ⛏️ Mining Rig Parts ({miningParts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'bruteforce' ? 'active' : ''}`}
          onClick={() => setActiveTab('bruteforce')}
        >
          💻 Brute Force Parts ({bruteForceParts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'network' ? 'active' : ''}`}
          onClick={() => setActiveTab('network')}
        >
          🌊 Network Injection ({networkParts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          🛡️ Firewall Defense ({securityParts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'malware' ? 'active' : ''}`}
          onClick={() => setActiveTab('malware')}
        >
          🦠 Malware Tools ({malwareParts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'sniffer' ? 'active' : ''}`}
          onClick={() => setActiveTab('sniffer')}
        >
          📡 Sniffer Tools ({snifferParts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'scanner' ? 'active' : ''}`}
          onClick={() => setActiveTab('scanner')}
        >
          🔍 Scanner Tools ({scannerParts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'defense_systems' ? 'active' : ''}`}
          onClick={() => setActiveTab('defense_systems')}
        >
          🛡️ Defense Systems ({defenseSystemParts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'offense_drones' ? 'active' : ''}`}
          onClick={() => setActiveTab('offense_drones')}
        >
          🚁 Offense Drones ({offenseDroneParts.length})
        </button>
      </div>

      {/* Purchase Status */}
      {purchaseStatus.visible && (
        <div className={`purchase-status ${purchaseStatus.type}`}>
          {purchaseStatus.message}
        </div>
      )}

      {/* Mining Rate Display - Only show for mining tab */}
      {activeTab === 'mining' && (
        <div className="mining-rate-display">
          <h3>⚡ Mining Rate Information</h3>
          <div className="rate-info">
            <div className="current-rate">
              <strong>Current Mining Rate:</strong> {formatMiningRate(getCurrentMiningRate())} WR coins/hour
              <br />
              <small>({(user?.partsInventory?.length || 0)} parts owned)</small>
            </div>
            <div className="future-rate">
              <strong>After Next Purchase:</strong> {formatMiningRate(getFutureMiningRate())} WR coins/hour
              <br />
              <small>+{formatMiningRate(getFutureMiningRate() - getCurrentMiningRate())} WR coins/hour increase</small>
            </div>
          </div>
          <div className="rate-details">
            <p><strong>Base Hash Rate:</strong> {BASE_HASH_RATE} H/s</p>
            <p><strong>Hash Rate Per Part:</strong> +{HASH_RATE_PER_PART} H/s per part</p>
            <p><strong>Base Rate:</strong> {formatMiningRate(BASE_HASH_RATE * COINS_PER_HASH_RATE)} WR coins/hour</p>
            <p><strong>Rate Per Part:</strong> +{formatMiningRate(HASH_RATE_PER_PART * COINS_PER_HASH_RATE)} WR coins/hour per part</p>
          </div>
        </div>
      )}

      {/* Mining Parts Tab */}
      {activeTab === 'mining' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>⛏️ Mining Rig Components</h3>
            <p>Upgrade your mining rig to increase WR Coin generation rate. Each part adds {HASH_RATE_PER_PART} H/s (+{formatMiningRate(HASH_RATE_PER_PART * COINS_PER_HASH_RATE)} WR coins/hour) to your mining rate.</p>
          </div>
          <div className="parts-grid">
            {miningParts.length > 0 ? (
              miningParts.map(renderPartCard)
            ) : (
              <div className="no-parts">No mining parts available</div>
            )}
          </div>
        </div>
      )}

      {/* Brute Force Parts Tab */}
      {activeTab === 'bruteforce' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>💻 Brute Force Hardware</h3>
            <p>Specialized equipment for breaking 6-digit codes on treasure chests and banks. Each part reduces brute force time by 1 day (base: 16-30 days).</p>
          </div>
          <div className="parts-grid">
            {bruteForceParts.length > 0 ? (
              bruteForceParts.map(renderPartCard)
            ) : (
              <div className="no-parts">No brute force parts available</div>
            )}
          </div>
        </div>
      )}

      {/* Network Injection Parts Tab */}
      {activeTab === 'network' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>🌊 Network Injection Tools</h3>
            <p>Advanced networking equipment for infiltrating computer systems. Each tool reduces network injection attack time by 1 hour (base: 6-12 hours).</p>
          </div>
          <div className="parts-grid">
            {networkParts.length > 0 ? (
              networkParts.map(renderPartCard)
            ) : (
              <div className="no-parts">No network injection tools available</div>
            )}
          </div>
        </div>
      )}

      {/* Firewall/Security Parts Tab */}
      {activeTab === 'security' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>🛡️ Firewall Defense Systems</h3>
            <p>Defensive security equipment to protect against network injection attacks. Each firewall part reduces the percentage of coins stolen by 1% (minimum 1%).</p>
          </div>
          <div className="parts-grid">
            {securityParts.length > 0 ? (
              securityParts.map(renderPartCard)
            ) : (
              <div className="no-parts">No firewall defense parts available</div>
            )}
          </div>
        </div>
      )}

      {/* Malware Parts Tab */}
      {activeTab === 'malware' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>🦠 Malware Attack Tools</h3>
            <p>Sophisticated malware development and deployment tools for casino vault security breaches. Each tool reduces malware attack time by 30 minutes (base: 12-16 hours).</p>
          </div>
          <div className="parts-grid">
            {malwareParts.length > 0 ? (
              malwareParts.map(renderPartCard)
            ) : (
              <div className="no-parts">No malware tools available</div>
            )}
          </div>
        </div>
      )}

      {/* Sniffer Tools Tab */}
      {activeTab === 'sniffer' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>📡 Sniffer Tools</h3>
            <p>Advanced signal detection and range enhancement equipment. 5 progressive sniffer tools available with consistent range bonuses. Base radius: 2000m. Max possible radius: 5,000m (2000m + 5×600m). Field Agents cannot purchase sniffer tools.</p>
            <div className="sniffer-info">
              <div className="sniffer-stats">
                <span>Current Radius: {(() => {
                  const baseRadius = 2000;
                  const snifferParts = user?.snifferInventory?.length || 0;
                  const bonusPerPart = 600;
                  const totalBonus = Math.min(snifferParts, 5) * bonusPerPart;
                  return baseRadius + totalBonus;
                })()}m</span>
                <span>Parts Owned: {user?.snifferInventory?.length || 0}/5</span>
                <span>Range Bonus: +{(() => {
                  const snifferParts = user?.snifferInventory?.length || 0;
                  const bonusPerPart = 600;
                  return Math.min(snifferParts, 5) * bonusPerPart;
                })()}m</span>
              </div>
            </div>
          </div>
          <div className="parts-grid">
            {snifferParts.length > 0 ? (
              snifferParts.map(renderPartCard)
            ) : (
              <div className="no-parts">No sniffer tools available</div>
            )}
          </div>
        </div>
      )}

      {/* Scanner Tools Tab */}
      {activeTab === 'scanner' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>🔍 Scanner Tools</h3>
            <p>Advanced network scanning and data extraction equipment. 10 progressive scanner tools available with consistent time reduction bonuses. Base scan time: 3 hours. Each part reduces scan time by 15 minutes (network & target scans). Brute force attacks: 13 hours base, reduced by 15 minutes per part.</p>
            <div className="scanner-info">
              <div className="scanner-stats">
                <span>Network Scan Time: {(() => {
                  const baseHours = 3;
                  const scannerParts = user?.scannerInventory?.length || 0;
                  const reductionPerPart = 0.25; // 15 minutes = 0.25 hours
                  const totalReduction = Math.min(scannerParts, 10) * reductionPerPart;
                  const finalTime = Math.max(baseHours - totalReduction, 0.5); // Minimum 30 minutes
                  return finalTime;
                })()}h</span>
                <span>Parts Owned: {user?.scannerInventory?.length || 0}/10</span>
                <span>Time Reduction: -{(() => {
                  const scannerParts = user?.scannerInventory?.length || 0;
                  const reductionPerPart = 15; // Minutes
                  return Math.min(scannerParts, 10) * reductionPerPart;
                })()}min</span>
                <span>Brute Force Time: {(() => {
                  const baseHours = 13;
                  const scannerParts = user?.scannerInventory?.length || 0;
                  const reductionPerPart = 0.25; // 15 minutes = 0.25 hours
                  const totalReduction = Math.min(scannerParts, 10) * reductionPerPart;
                  const finalTime = Math.max(baseHours - totalReduction, 2); // Minimum 2 hours
                  return finalTime;
                })()}h</span>
              </div>
            </div>
          </div>
          <div className="parts-grid">
            {scannerParts.length > 0 ? (
              scannerParts.map(renderPartCard)
            ) : (
              <div className="no-parts">No scanner tools available</div>
            )}
          </div>
        </div>
      )}

      {/* Defense Systems Tab */}
      {activeTab === 'defense_systems' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>🛡️ Defense Systems</h3>
            <p>Advanced compound defense equipment for creating secure perimeters around your assets. Build fortress cores, defense towers, and radar stations to protect your properties.</p>
          </div>
          <div className="parts-grid">
            {defenseSystemParts.length > 0 ? (
              defenseSystemParts.map(renderPartCard)
            ) : (
              <div className="no-parts">No defense systems available</div>
            )}
          </div>
        </div>
      )}

      {/* Offense Drones Tab */}
      {activeTab === 'offense_drones' && (
        <div className="parts-section">
          <div className="section-header">
            <h3>✈️ Offense Drones</h3>
            <p>Advanced military drones for reconnaissance and assault operations. Requires a command center to purchase. Scout drones provide intelligence gathering, assault drones destroy defense towers, and nuke drones can eliminate command centers.</p>
            <div className="drone-requirements">
              <h4>🏛️ Purchase Requirements:</h4>
              <ul>
                <li>• Command center must be deployed before purchasing any drones</li>
                <li>• No daily purchase limits - buy unlimited drones</li>
                <li>• Scout Drone: 20,000 XP + 1,000 WR Coins</li>
                <li>• Assault Drone: 20,000 XP + 500 WR Coins</li>
                <li>• Nuke Drone: 25,000 XP + 2,000 WR Coins</li>
                <li>• Maritime Scout Drone: 15,000 XP + 5,000 WR Coins (Water-only deployment)</li>
              </ul>
            </div>
          </div>
          
          {droneLoading ? (
            <div className="loading">Loading drone purchases...</div>
          ) : (
            <div className="parts-grid">
              {availableDronePurchases.length > 0 ? (
                availableDronePurchases.map(renderDroneCard)
              ) : (
                <div className="no-parts">
                  <p>No drones available for purchase.</p>
                  <p>You need a command center to purchase drones.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* User Stats */}
      <div className="user-stats" key={`stats-${lastPurchaseTime}`}>
        <div className="stat">
          <span className="stat-label">💰 WR Coins:</span>
          <span className="stat-value">{user?.wrCoins || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">⭐ XP:</span>
          <span className="stat-value">{user?.xp || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">⛏️ Mining Parts:</span>
          <span className="stat-value">{user?.partsInventory?.length || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">💻 Brute Force Parts:</span>
          <span className="stat-value">{user?.bruteForceInventory?.length || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">🌊 Network Injectors:</span>
          <span className="stat-value">{user?.networkInjectorTools || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">🛡️ Firewall Parts:</span>
          <span className="stat-value">{user?.firewallParts || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">🦠 Malware Tools:</span>
          <span className="stat-value">{user?.malwareInventory?.length || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">📡 Sniffer Tools:</span>
          <span className="stat-value">{user?.snifferInventory?.length || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">🔍 Scanner Tools:</span>
          <span className="stat-value">{user?.scannerInventory?.length || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">🛡️ Defense Systems:</span>
          <span className="stat-value">{user?.defenseInventory?.length || 0}</span>
        </div>
        <div className="stat">
          <span className="stat-label">✈️ Offense Drones:</span>
          <span className="stat-value">{user?.droneInventory?.length || 0}</span>
        </div>
      </div>
    </div>
    </div>
  );
};

export default Parts;